import { type JSX } from "react";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { requiredValidation } from "@via/utils/form-validators.tsx";
import { useGetSavedCredentials } from "@via/pages/account/profile/profile.api.tsx";
import { CredentialView } from "common";
import SelectComponent from "@via/components/select/select.tsx";

export default function CreateBitbucketToken({ control, errors }): JSX.Element {
  const { data: savedCredentials, isLoading: isSavedCredentialsLoading } =
    useGetSavedCredentials({ type: CredentialView.UserOwned });
  const sshCredentials =
    (savedCredentials?.ssh || []).map(({ id, name }) => ({
      label: name,
      value: id,
    })) ?? [];

  return (
    <>
      <FormInputComponent
        control={control}
        name="name"
        label="Token Name"
        placeholder="Enter Token Name"
        validations={requiredValidation("Token Name")}
        showRequiredField
      />
      <FormInputComponent
        control={control}
        name="credentialData.username"
        label="Account Username"
        placeholder="Enter Account Username"
        validations={requiredValidation("Account Username")}
        showRequiredField
      />
      <FormInputComponent
        control={control}
        name="credentialData.email"
        label="Account Email"
        placeholder="Enter Your Account Email"
        validations={requiredValidation("Email")}
        showRequiredField
      />

      <div className="mb-4">
        <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
          SSH Keys <span className="text-red-400">*</span>
        </label>
        <SelectComponent
          name="credentialData.sshId"
          control={control}
          rules={requiredValidation("SSH Key")}
          options={sshCredentials}
          isLoading={isSavedCredentialsLoading}
          enableClear={false}
          placeholder={"Select SSH Keys"}
        />
        {errors.sshId && (
          <span className="text-xs text-rose-500">{errors.sshId.message}</span>
        )}
      </div>

      <BitbucketSSHInstructions />
    </>
  );
}

function BitbucketSSHInstructions(): JSX.Element {
  return (
    <div className="mt-3 text-xs text-gray-500">
      <p className="mb-3">Setting up SSH Keys for Bitbucket</p>

      <div className="mb-3">
        <p className="mb-1">Foundry Connect:</p>
        <ol className="list-decimal ml-6">
          <li>
            Log in to your Foundry Connect account and go to the
            &quot;Profile&quot; section.
          </li>
          <li>
            Select the &quot;Credentials&quot; tab and click on the &quot;Create
            Credential&quot; button.
          </li>
          <li>
            Select the &quot;SSH&quot; option on the &quot;Credential Type&quot;
            dropdown.
          </li>
          <li>Enter a name for this SSH credential.</li>
          <li>Click on the &quot;Generate Keys for Me&quot; button.</li>
          <li>
            Copy the generated Public Key and click the &quot;Save&quot; button.
          </li>
          <li>
            Navigate to the &quot;Integration Tokens&quot; tab and select the
            SSH key you just added while creating an account.
          </li>
        </ol>
      </div>

      <div className="mb-3">
        <p className="mb-1">Bitbucket:</p>
        <ol className="list-decimal ml-6">
          <li>
            Open Bitbucket and select the desired repository. Click on the
            &quot;Repository Settings&quot; option.
          </li>
          <li>Look for the &quot;Access Keys&quot; option and click on it.</li>
          <li>Click on the &quot;Add key&quot; button.</li>
          <li>Copy the public key that was generated in Foundry Connect.</li>
          <li>
            Paste the copied public key into the &quot;SSH Key&quot; field in
            Bitbucket.
          </li>
        </ol>
      </div>

      <div>
        <p>
          By following these steps, you will have successfully created and
          linked the SSH key pair between Foundry Connect and Bitbucket. This
          will allow you to create and update repositories in Bitbucket using
          the SSH keys from Foundry Connect.
        </p>
      </div>
    </div>
  );
}
